import { get, merge } from 'lodash';
import { isMapType } from 'constants/chart-roles';
import { HEATMAP_DEFAULTS } from 'constants/geospatial';

/**
 * Generates a color mapping for each unique doc in the 'color' encoding channel
 * Loops around once the colors run out
 *
 * If there is no 'color' encoding channel, then there will only be one key:value in the colorMapping
 * object returned
 *
 * @param  {Object} stageData  Object containing documents returned from the agg pipeline, as well as metadata about the chart type
 * @return {Object}            Object containing metadata with a color mapping for each doc in the 'color' channel
 */
function generateColorMapping(stageData) {
  const {
    environment: { channels },
    documents,
    spec: { colors },
  } = stageData;

  if (!colors) {
    return {};
  }

  let count = 0;

  // If we don't have the color channel, return early
  if (!channels.color) {
    return { undefined: colors[0] };
  }

  const colorMapping = documents.reduce((mapping, doc) => {
    if (!mapping[doc.color]) {
      mapping[doc.color] = colors[count % colors.length];
      count++;
    }

    return mapping;
  }, {});

  return colorMapping;
}

function generateHeatmapMetadata(stageData) {
  const {
    environment: { channels },
    spec: { blur, radius, gradient, opacity },
  } = stageData;
  const aggregateType = get(channels, 'intensity.aggregate', 'sum');

  const metadata = merge(
    { ...HEATMAP_DEFAULTS, aggregateType },
    { blur, radius, opacity }
  );

  if (gradient) {
    metadata.gradient = gradient;
  }

  return metadata;
}

function generateViewportMetadata(stageData) {
  const {
    environment: { meta },
  } = stageData;

  return meta ? meta.viewport : {};
}

/**
 * Generate all needed metadata for map chart types
 *
 * @param {Object} stageData Object containing documents returned from the agg pipeline, as well as metadata about the chart type
 * @return {Object}          Object containing metadata for maps
 */
function generateMapMetadata(stageData) {
  const {
    environment: { chartType, customisations },
    metadata,
  } = stageData;

  if (!isMapType(chartType)) {
    return {};
  }

  const colorMapping = generateColorMapping(stageData);
  const heatmap = generateHeatmapMetadata(stageData);
  const viewport = generateViewportMetadata(stageData);
  const autoViewport = get(
    customisations,
    'options.autoViewport.enabled',
    true
  );

  return {
    metadata: {
      ...metadata,
      colorMapping,
    },
    spec: {
      ...stageData.spec,
      viewport,
      autoViewport,
      ...heatmap,
    },
  };
}

export default {
  input: [
    'documents',
    'metadata',
    'spec',
    'environment.channels',
    'environment.chartType',
    'environment.meta',
    'environment.customisations.options.autoViewport.enabled',
  ],
  fn: generateMapMetadata,
};
export { generateMapMetadata };
